
import * as React from 'react'
import { withRequiredPermission } from '@/components/auth/with-required-permission'
import { PERMISSIONS } from '@/hooks/use-perm'
import { Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { useTranslationsList } from '@/lib/api/translations'
import { TranslationsTable } from './components/TranslationsTable'
import { MainTableState } from '@/lib/utils/table-state-utils'
import { useStoreNavigate } from '@/hooks/use-app-navigate'
import { tableStateToDrizzQuery } from './utils/table-state-to-drizz-query'
import { CreateTranslationDialog } from './components/create-translation-dialog'

/**
 * Store settings page - Languages/Translations section
 * Displays translations for a specific store
 */
function LanguagesPageInner() {
    const { t } = useTranslation(['languages', 'common'])
    const { id, storeRoutes, navigate } = useStoreNavigate()

    const [createDialogOpen, setCreateDialogOpen] = React.useState(false)

    // Table state managed by TranslationsTable
    const [tableState, setTableState] = React.useState<MainTableState>({
        pagination: { pageIndex: 0, pageSize: 20 },
        sorting: [{ id: 'createdAt', desc: true }],
        columnFilters: [],
    })

    // Convert table state to drizzQuery format
    const queryInput = React.useMemo(() => {
        return tableStateToDrizzQuery(tableState, id)
    }, [tableState, id])

    // Use API hook to fetch translations
    const {
        data: translationsResponse,
        isLoading: loading,
        error,
        refetch,
    } = useTranslationsList(queryInput)

    const translations = translationsResponse?.items || []
    const total = translationsResponse?.total || 0

    // Get existing locales to exclude from selection
    const existingLocales = React.useMemo(() => {
        return translations.map((t) => t.locale).filter(Boolean)
    }, [translations])

    const handleCreateSuccess = React.useCallback(
        (translationId: string) => {
            // Refetch translations list
            refetch()

            // Navigate to edit page
            if (storeRoutes.TRANSLATION_EDIT) {
                navigate(storeRoutes.TRANSLATION_EDIT(translationId))
            }
        },
        [refetch, navigate, storeRoutes]
    )

    return (
        <div className="flex flex-col space-y-4 sm:space-y-6">
            {/* Page header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                <div>
                    <h1 className="text-xl font-semibold sm:text-2xl">{t('title')}</h1>
                    <p className="text-muted-foreground text-sm">{t('description')}</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button className="w-full sm:w-auto" onClick={() => setCreateDialogOpen(true)}>
                        <Plus className="mr-2 h-4 w-4" />
                        {t('addTranslation')}
                    </Button>
                </div>
            </div>

            {/* Translations table */}
            <TranslationsTable
                translations={translations}
                total={total}
                loading={loading}
                onTableStateChange={setTableState}
                onRefetch={refetch}
            />

            {/* Error message */}
            {error && (
                <div className="mt-4 rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700">
                    {error instanceof Error ? error.message : t('loadError')}
                </div>
            )}

            {/* Create Translation Dialog */}
            <CreateTranslationDialog
                open={createDialogOpen}
                onOpenChange={setCreateDialogOpen}
                existingLocales={existingLocales}
                storeId={id}
                onSuccess={handleCreateSuccess}
            />
        </div>
    )
}

export const LanguagesPage = withRequiredPermission(LanguagesPageInner, PERMISSIONS.STORES_VIEW)